import { WishlistStateController } from "./controllers/WishlistStateController";

import type { ReactiveController } from "lit";
import type { State, StateConfig } from "./controllers/WishlistStateController";
import type { ProductInfo } from "./state/WishlistState";
import type { Wishlist, WishlistItem } from "./api/WishlistApi";
import type { Subscription } from "./state/Observable";

export { WishlistStateController };

export class WishlistElementHeadless {
  subscription?: Subscription;
  observer?: IntersectionObserver;
  connected = false;
  stateCtrl: WishlistStateController;
  cleanupMethods: Array<() => void> = [];
  eventPending = false;
  firstConnect = true;
  host: HTMLElement;

  wishlist?: Wishlist;
  productInfo?: ProductInfo;
  wishlistItem?: WishlistItem;

  get app() {
    return this.stateCtrl.app;
  }

  static get observedAttributes() {
    return [
      "data-wishlist-id",
      "data-wishlist-item-id",
      "data-product-handle",
      "data-product-id",
      "data-variant-id",
    ];
  }

  constructor(host: HTMLElement) {
    this.host = host;
    this.stateCtrl = new WishlistStateController(this);

    if (this.host.hasAttribute("wk-headless")) {
      throw new Error("Headless component already initialised");
    }

    this.host.setAttribute("wk-headless", "");
    this.host.setAttribute("wk-skip", "");
    this.initEvents();
    this.connect();
    this.firstConnect = false;

    const observer = new MutationObserver((mutations) => {
      const hasAttributeChange = mutations.some(
        (mutation) =>
          mutation.type === "attributes" &&
          mutation.attributeName &&
          WishlistElementHeadless.observedAttributes.includes(
            mutation.attributeName,
          ),
      );
      if (hasAttributeChange) {
        this.initState();
      }
    });

    observer.observe(this.host, {
      attributes: true,
    });
  }

  addController(controller: ReactiveController) {}

  appReadyCallback() {
    if (!this.host.dataset.productHandle) {
      return;
    }

    this.app.events.subscribe("product:change-variant:success", (event) => {
      if (
        this.host.dataset.productHandle === event.data?.productHandle &&
        event.data?.variantId
      ) {
        const wishlistItemId = this.productInfo
          ? this.productInfo.wishlistItemId
          : undefined;

        const currentVariantId = this.productInfo
          ? this.productInfo.variantId
          : undefined;

        if (wishlistItemId && !currentVariantId) {
          this.app.updateWishlistItem({
            wishlistItemId,
            changes: {
              variantId: event.data?.variantId,
            },
          });
        }

        this.host.dataset.variantId = event.data.variantId.toString();

        if (this.connected) {
          this.initState();
        }
      }
    });
  }

  async connect() {
    if (this.connected) {
      return;
    }

    this.connected = true;
    this.init();
  }

  getStateConfig(): StateConfig & { params?: Record<string, string> } {
    return {};
  }

  init() {
    const { loading } = this.getStateConfig();
    if (loading === "lazy") {
      this.initFallbackState();
      this.initObserver();
    } else {
      this.initState();
      this.appReadyCallback();
    }
  }

  updated() {}

  setState(state: Partial<State>) {
    this.wishlist = state.wishlist;
    this.wishlistItem = state.wishlistItem;
    this.productInfo = state.productInfo;
    this.updated();
  }

  createRenderRoot() {
    return this;
  }

  initState() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.subscription = this.stateCtrl.observe().subscribe((state) => {
      this.setState(state);
    });
  }

  initFallbackState() {
    const fallback = this.stateCtrl.getFallback();

    if (fallback) {
      this.setState(fallback);
    }
  }

  initObserver() {
    this.observer?.disconnect();

    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersect(entries),
      {
        root: null,
        rootMargin: "0px",
      },
    );

    this.host.setAttribute("wk-lazy", "");

    this.observer.observe(this.host);
  }

  handleIntersect([entry]: IntersectionObserverEntry[]) {
    if (entry.isIntersecting) {
      this.host.removeAttribute("wk-lazy");
      this.observer?.disconnect();
      this.initState();
      this.appReadyCallback();
    }
  }

  getEventConfig(): EventConfig {
    return {};
  }

  initEvents() {
    const eventConfig = this.getEventConfig();

    if (!eventConfig) {
      return;
    }

    const subscriptions = Object.entries(eventConfig).map(([key, handler]) => {
      const [eventName, selector] = key.split(/ (.*)/s);

      return this.subscribeToEvent({
        eventName,
        selector,
        handler,
      });
    });

    // TODO: Events need to be cleaned up?
    // this.cleanupMethods.push(() =>
    //   subscriptions.forEach((subscription) => subscription.unsubscribe())
    // );
  }

  subscribeToEvent({
    eventName,
    handler,
    selector,
  }: {
    eventName: string;
    selector?: string;
    handler: (event: Event, target?: EventTarget | null) => Promise<any> | any;
  }) {
    this.host.addEventListener(eventName, async (event) => {
      if (this.eventPending) {
        event.preventDefault();
        return;
      }

      this.eventPending = true;

      const findEventTarget = () => {
        if (!(event.target instanceof HTMLElement)) {
          return null;
        }
        return selector
          ? event.target.closest(selector)
          : (event.currentTarget as HTMLElement);
      };

      const target = findEventTarget();

      // if (!target) {
      //   this.eventPending = false;
      //   return;
      // }

      const executeEventHandler = async () => {
        try {
          return handler.bind(this)(event, target);
        } catch (error) {
          console.error(error);
          return error;
        }
      };

      await executeEventHandler();
      this.eventPending = false;
    });
  }
}

export type EventConfig = Record<string, EventHandler>;
export type EventHandler = (data: Event | Element) => void;
